<template>
  <div :class="$style.container">
    <!-- タイトル -->
    <div :class="$style.container__block">
      <p :class="$style.container__title">カレンダー名</p>
      <p :class="$style.container__subtitle">
        ※ 将来、ユーザーの予約画面に表示する可能性があります
      </p>
      <DolphinTextInput
        :class="$style.titleInput"
        v-model="model.title"
        placeholder="例) 未来を見据えた住まい選び！予算とライフプランの作り方"
      />
      <p v-if="errors?.title" :class="$style.error">{{ errors.title }}</p>
    </div>

    <!-- 所要時間 -->
    <div :class="$style.container__block">
      <p :class="$style.container__title">1枠の所要時間</p>
      <div :class="$style.timeSelect">
        <ul :class="[$style.timeSelect__radioButton, $style.radioButton]">
          <li v-for="(option, index) in durationMinutesOptions" :key="index">
            <DolphinRadioButtonInContainer
              :class="$style.radioButton__option"
              name="durationMinutes"
              v-model="selectedDurationMinutes"
              :value="option.value"
            >
              {{ option.label }}
            </DolphinRadioButtonInContainer>
          </li>
        </ul>
        <div
          v-if="selectedDurationMinutes === 'custom'"
          :class="$style.customInput"
        >
          <DolphinTextInput
            :class="$style.customInput__box"
            v-model="customDurationMinutes"
            type="number"
          />
          <span :class="$style.customInput__text">分</span>
        </div>
      </div>
      <p v-if="errors?.durationMinutes" :class="$style.error">
        {{ errors.durationMinutes }}
      </p>
    </div>

    <!-- 表示間隔 -->
    <div :class="$style.container__block">
      <p :class="$style.container__title">枠の表示間隔</p>
      <p :class="$style.container__subtitle">
        自動、または所要時間と同じ値を選択すると枠が隙間なく表示されます。<br />
        値が所要時間よりも短いと枠が重複して表示されます。
      </p>
      <div :class="$style.timeSelect">
        <ul :class="[$style.timeSelect__radioButton, $style.radioButton]">
          <li v-for="(option, index) in offsetMinutesOptions" :key="index">
            <DolphinRadioButtonInContainer
              :class="$style.radioButton__option"
              name="offsetMinutes"
              v-model="selectedOffsetMinutes"
              :value="option.value"
            >
              {{ option.label }}
            </DolphinRadioButtonInContainer>
          </li>
        </ul>
        <div
          v-if="selectedOffsetMinutes === 'custom'"
          :class="$style.customInput"
        >
          <DolphinTextInput
            :class="$style.customInput__box"
            v-model="customOffsetMinutes"
            type="number"
          />
          <span :class="$style.customInput__text">分</span>
        </div>
      </div>
      <p v-if="errors?.offsetMinutes" :class="$style.error">
        {{ errors.offsetMinutes }}
      </p>
    </div>

    <!-- 表示可能日時 -->
    <div :class="$style.container__block">
      <p :class="$style.container__title">表示する時間範囲</p>
      <p :class="$style.container__subtitle">
        空きスケジュールを表示する時間帯を設定することができます。<br />
        範囲外の時間は日程調整時の候補として表示されなくなります。
      </p>
      <CalendarDailyAvailabilityTable
        :class="$style.dailyAvailability"
        v-model="model.dailyAvailabilities"
      />
    </div>

    <!-- staff の割り当て -->
    <div :class="$style.container__block">
      <p :class="$style.container__title">カレンダーメンバー</p>
      <p :class="$style.container__subtitle">
        担当者は日程調整時に予定の空いている人がランダムに1人選択されます。<br />
        ※ 下記候補に表示されるのはカレンダー連携している人です
      </p>
      <DolphinMultiSelector
        :class="$style.staffSelect"
        v-model="assignedStaffs"
        :options="staffOptions"
        track-by="guid"
        label="displayName"
        :loading="staffsLoading"
      />
      <p v-if="errors?.assignedStaffGUIDs" :class="$style.error">
        {{ errors.assignedStaffGUIDs }}
      </p>
    </div>

    <!-- カレンダーの公開・非公開ステータス -->
    <div :class="$style.container__block">
      <p :class="$style.container__title">カレンダーの状態</p>
      <p :class="$style.container__subtitle">
        非公開にすると日程調整カレンダーの指定画面で候補として表示されなくなります。
      </p>
      <p :class="$style.container__warning">
        ※
        非公開にすると設定済みの日程調整でユーザーが予約できなくなります。非公開にする場合は、他で設定されていないか必ず確認してください。
      </p>
      <div :class="$style.statusSelect">
        <ul :class="[$style.statusSelect__radioButton, $style.radioButton]">
          <li v-for="option in statusOptions" :key="option.value">
            <DolphinRadioButtonInContainer
              :class="$style.radioButton__option"
              name="status"
              v-model="calendarStatus"
              :value="option.value"
            >
              {{ option.label }}
            </DolphinRadioButtonInContainer>
          </li>
        </ul>
      </div>
    </div>
    <CalendarStatusModal disable-backdrop>
      <ConfirmationModal
        title="カレンダーの状態を 非公開 へ変更"
        buttonText="非公開にする"
        @confirm="handleConfirm"
        @close="handleClose"
      >
        <p>
          非公開にすると、
          <span :class="$style.warning"
            >設定済みの日程調整でユーザーが予約できなくなります</span
          >
          。<br />
          本当に非公開にしてもよろしいですか？
        </p>
      </ConfirmationModal>
    </CalendarStatusModal>

    <!-- カレンダーの説明 -->
    <div :class="$style.container__block">
      <p :class="$style.container__title">カレンダーの説明</p>
      <p :class="$style.container__subtitle">
        ※ 将来、ユーザーの予約画面に表示する可能性があります
      </p>
      <CountingTextarea
        :class="$style.descriptionInput"
        :rows="8"
        v-model="model.description"
      />
    </div>
  </div>
</template>

<script setup lang="ts">
import { computed, ref, watch } from 'vue'

import DolphinTextInput from '@/components/atoms/DolphinTextInput.vue'
import DolphinMultiSelector from '@/components/atoms/DolphinMultiSelector.vue'
import DolphinRadioButtonInContainer from '@/components/molecules/DolphinRadioButtonInContainer.vue'
import CountingTextarea from '@/components/molecules/CountingTextarea.vue'
import ConfirmationModal from '@/components/molecules/ConfirmationModal.vue'
import { useModal } from '@/hooks/useModal.ts'

import { type FormType, validateForm } from './formSchema'
import { statusOptions } from './calendar.ts'
import CalendarDailyAvailabilityTable from './CalendarDailyAvailabilityTable.vue'
import { useCalendarFormStaffsQuery } from './CalendarForm.generated.ts'

const model = defineModel<FormType>({ required: true })

const errors = computed(() => validateForm(model.value))

/****************************************************************
 * Slot Duration Minutes
 ****************************************************************/

const selectedDurationMinutes = ref<number | string>(
  model.value.durationMinutes
)
const customDurationMinutes = ref<number>(60)

const durationMinutesOptions = [
  {
    label: '30分',
    value: 30,
  },
  {
    label: '50分',
    value: 50,
  },
  {
    label: '60分',
    value: 60,
  },
  {
    label: 'カスタム',
    value: 'custom',
  },
]

watch([selectedDurationMinutes, customDurationMinutes], newVal => {
  if (typeof newVal[0] === 'number') {
    model.value.durationMinutes = newVal[0]
  } else {
    model.value.durationMinutes = newVal[1]
  }
})

/****************************************************************
 * Slot Offset Minutes
 ****************************************************************/

const selectedOffsetMinutes = ref<number | string>(
  model.value.offsetMinutes === model.value.durationMinutes
    ? 'auto'
    : model.value.offsetMinutes
)
const customOffsetMinutes = ref<number>(60)

const offsetMinutesOptions = [
  {
    label: '自動',
    value: 'auto',
  },
  {
    label: '30分',
    value: 30,
  },
  {
    label: '60分',
    value: 60,
  },
  {
    label: 'カスタム',
    value: 'custom',
  },
]

watch([selectedOffsetMinutes, customOffsetMinutes], newVal => {
  if (typeof newVal[0] === 'number') {
    model.value.offsetMinutes = newVal[0]
    return
  }

  if (newVal[0] === 'auto') {
    model.value.offsetMinutes = model.value.durationMinutes
    return
  }

  model.value.offsetMinutes = newVal[1]
})

/****************************************************************
 * Calendar Status
 ****************************************************************/
const {
  CalendarStatusModal,
  openCalendarStatusModal,
  closeCalendarStatusModal,
} = useModal('calendarStatus')

const calendarStatus = computed({
  get: () => {
    return model.value.status
  },

  set: newVal => {
    if (newVal === model.value.status) {
      return
    }

    // 条件によって値の変更を行わない処理にした場合、
    // radio button が押せなくなるので条件に依らず値をセットする
    model.value.status = newVal

    if (newVal === 'INACTIVE') {
      // INACTIVE の場合は確認モーダルを表示し、その先の回答に応じて値をセットする
      openCalendarStatusModal()
    }
  },
})

// 確認ボタンが押されず close が押された場合は 'ACTIVE' に戻す
const handleClose = () => {
  model.value.status = 'ACTIVE'
  closeCalendarStatusModal()
}

// 確認ボタンが押された場合はそのまま
const handleConfirm = () => {
  closeCalendarStatusModal()
}

/****************************************************************
 * Staffs
 ****************************************************************/

const { result: staffsResult, loading: staffsLoading } =
  useCalendarFormStaffsQuery({
    // 高速に表示したいのと、極力最新の情報を表示したいので、キャッシュとネットワークの両方から取得
    fetchPolicy: 'cache-and-network',
  })

const staffOptions = computed(() => {
  if (!staffsResult.value) {
    return []
  }

  // カレンダー連携しているスタッフのみ表示
  return staffsResult.value.staffsByPlanner
    .filter(staff => staff.connectedThirdPartyProvider)
    .map(staff => ({
      guid: staff.guid,
      displayName: staff.displayName,
    }))
})

const assignedStaffs = computed({
  get() {
    return staffOptions.value.filter(staff =>
      model.value.assignedStaffGUIDs.includes(staff.guid)
    )
  },
  set(newValue) {
    model.value.assignedStaffGUIDs = newValue.map(staff => staff.guid)
  },
})
</script>

<style lang="scss" module>
.error {
  color: $color-caution-accent;
  font-size: 12px;

  &:nth-child(n + 2) {
    margin-top: 4px;
  }
}

.warning {
  color: $color-caution-accent;
  font-weight: bold;
}

.container {
  &__block {
    &:nth-child(n + 2) {
      margin-top: 40px;
    }
  }

  &__title {
    font-size: 18px;
    font-weight: bold;
    color: $color-text-and-object-medium-emphasis;
  }

  &__subtitle {
    font-size: 12px;
    color: $color-text-and-object-high-emphasis;
    margin-top: 8px;
  }

  &__warning {
    font-size: 12px;
    color: $color-caution-accent;
    margin-top: 8px;
  }
}

.titleInput {
  margin-top: 16px;
  width: 100%;
}

.timeSelect {
  display: flex;
  align-items: flex-end;
  gap: 8px;

  &__radioButton {
    display: flex;
    gap: 2px;
    margin-top: 16px;
  }
}

.radioButton {
  &__option {
    display: flex;
    flex-direction: row;
    align-items: center;
    justify-content: center;
    width: 90px;
  }
}

.customInput {
  &__box {
    width: 90px;
    line-height: 1.4;
  }

  &__text {
    margin-left: 12px;
  }
}

.dailyAvailability {
  margin-top: 16px;
}

.staffSelect {
  margin-top: 16px;
  width: 60%;
}

.statusSelect {
  display: flex;
  align-items: center;

  &__radioButton {
    display: flex;
    gap: 16px;
    margin-top: 16px;
  }
}

.descriptionInput {
  margin-top: 16px;
}
</style>
